/* ============================================================
   hab-countdown.js — HAB shared countdown timer (Avone v4) · Wave 5,
   Wave 8 Push 3.2 parser extension (14 Jul 2026).

   Vanilla, no build step, IIFE. Powers hab-sale-collections and
   hab-sale-hero (any element with [data-hab-countdown]).

   Contract (set by the section Liquid on the host element):
     data-hab-countdown            — marker (presence = init)
     data-end-iso="…"              — target wall-clock (see accepted
                                     formats below). Interpreted as
                                     Europe/London (BST/GMT auto-
                                     resolved via Intl).
     data-tz="Europe/London"       — timezone override (rare)
     data-expired-text="…"         — shown on expiry (optional; default
                                     "This sale has ended")

   Accepted `data-end-iso` formats (parse order — first match wins):
     1. Offset-suffixed ISO       "2026-07-31T23:59:00Z"
                                  "2026-07-31T23:59:00+01:00"
     2. Bare wall-clock ISO       "2026-07-31T23:59:00"
                                  "2026-07-31T23:59"        (seconds opt.)
                                  "2026-07-31 23:59"        (space OK)
     3. Date-only ISO             "2026-07-31"              → defaults to 23:59
     4. UK slash DD/MM/YYYY       "31/07/2026"              → defaults to 23:59
     5. UK slash DD/MM/YY         "31/07/26"                → 20YY if YY<70,
                                                             else 19YY
     6. UK slash DD/MM/YYYY HH:MM "31/07/2026 23:59"
        or DD/MM/YY HH:MM
     7. Anything else             → NaN + console.warn (silent-expired
                                     was worst failure mode pre-Wave 8.3)

   Bounds enforced on every non-Date.parse path:
     - Month 1-12, Day 1-31, Hour 0-23, Minute 0-59, Second 0-59
     - Round-trip validation catches leap-year and short-month oversteps
       (e.g. 31/02/2026 → NaN, not silent March rollover)

   UK-first ambiguity: `05/07/2026` = 5 July (day/month/year).
   For merchants with US-style MM/DD input, the first group > 12 makes
   it unambiguous DD/MM; both ≤ 12 always resolves UK-first.

   Timezone honesty: the merchant types a London wall-clock time with no
   offset. We resolve that to an absolute UTC instant (accounting for BST)
   via Intl, then count down against Date.now(). So a "60 seconds left"
   readout is correct for a visitor in London, New York or Tokyo alike —
   they all watch the same instant tick to zero.

   Reduced-motion: the per-second pulse is suppressed; the numbers still
   update every second.
   ============================================================ */
(function(){
  'use strict';

  var DEFAULT_EXPIRED = 'This sale has ended';
  var UNITS = [
    { key: 'days',    label: 'Days' },
    { key: 'hours',   label: 'Hours' },
    { key: 'minutes', label: 'Minutes' },
    { key: 'seconds', label: 'Seconds' }
  ];

  var reduce = false;
  try { reduce = matchMedia('(prefers-reduced-motion: reduce)').matches; } catch(e){}

  // ── offset (ms) of a named tz at a given absolute instant: (tz wall clock − UTC) ──
  function tzOffsetMs(date, tz){
    try{
      var dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      var map = {};
      dtf.formatToParts(date).forEach(function(p){ map[p.type] = p.value; });
      var h = map.hour === '24' ? 0 : parseInt(map.hour, 10);
      var asUTC = Date.UTC(+map.year, map.month - 1, +map.day, h, +map.minute, +map.second);
      return asUTC - date.getTime();
    }catch(e){ return 0; }
  }

  // ── log invalid input to devtools + return NaN ──
  function warnAndFail(rawInput){
    if(typeof console !== 'undefined' && console.warn){
      console.warn('[hab-countdown] Invalid date "' + rawInput + '" — expected YYYY-MM-DD HH:MM (London time).');
    }
    return NaN;
  }

  // ── validate + build UTC epoch from wall-clock parts in given tz ──
  function buildEpoch(year, month, day, hour, minute, second, zone, rawInput){
    // Bounds
    if(month < 1 || month > 12) return warnAndFail(rawInput);
    if(day < 1   || day > 31)   return warnAndFail(rawInput);
    if(hour < 0  || hour > 23)  return warnAndFail(rawInput);
    if(minute<0  || minute>59)  return warnAndFail(rawInput);
    if(second<0  || second>59)  return warnAndFail(rawInput);
    // Round-trip check — catches leap-year + short-month oversteps
    // (e.g. Date.UTC(2026, 1, 31) rolls forward to Mar 3, but we want NaN).
    var probeUTC = Date.UTC(year, month - 1, day, hour, minute, second);
    var probe = new Date(probeUTC);
    if(probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day){
      return warnAndFail(rawInput);
    }
    var off = tzOffsetMs(probe, zone);
    return probeUTC - off;
  }

  // ── string → absolute epoch ms, with graceful UK-format fallbacks ──
  function isoToEpoch(iso, tz){
    var rawInput = iso; // preserve original for the warning
    if(!iso) return NaN; // empty → silent skip (means "no countdown", not invalid)
    var s = String(iso).trim();
    var zone = tz || 'Europe/London';

    // Path 1 — offset-suffixed ISO (Z or ±HH:MM / ±HHMM)
    if(/(Z|[+-]\d{2}:?\d{2})$/.test(s)){
      var abs = Date.parse(s);
      if(!isNaN(abs)) return abs;
      return warnAndFail(rawInput);
    }

    // Path 2 — bare wall-clock ISO: YYYY-MM-DD[T ]HH:MM(:SS)?  (anchored)
    var m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if(m2){
      return buildEpoch(+m2[1], +m2[2], +m2[3], +m2[4], +m2[5], +(m2[6] || 0), zone, rawInput);
    }

    // Path 3 — date-only ISO: YYYY-MM-DD  → default 23:59:00
    var m3 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if(m3){
      return buildEpoch(+m3[1], +m3[2], +m3[3], 23, 59, 0, zone, rawInput);
    }

    // Paths 4/5/6 — UK slash DD/MM/YYYY | DD/MM/YY | + optional HH:MM
    // First group = day, second = month (UK-first). Bounds-check in buildEpoch.
    var m4 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})(?:[T ](\d{1,2}):(\d{2}))?$/);
    if(m4){
      var dd = +m4[1], mm = +m4[2], yy = +m4[3];
      var year = m4[3].length === 2 ? (yy < 70 ? 2000 + yy : 1900 + yy) : yy;
      var hh   = m4[4] !== undefined ? +m4[4] : 23;
      var mn   = m4[5] !== undefined ? +m4[5] : 59;
      return buildEpoch(year, mm, dd, hh, mn, 0, zone, rawInput);
    }

    return warnAndFail(rawInput);
  }

  function pad(n){ return (n < 10 ? '0' : '') + n; }

  function build(host){
    var grid = document.createElement('div');
    grid.className = 'hab-cd-grid';
    var refs = {};
    UNITS.forEach(function(u, i){
      if(i > 0){
        var sep = document.createElement('span');
        sep.className = 'hab-cd-sep';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = ':';
        grid.appendChild(sep);
      }
      var seg = document.createElement('div');
      seg.className = 'hab-cd-seg';
      var num = document.createElement('span');
      num.className = 'hab-cd-num';
      num.textContent = '00';
      var lbl = document.createElement('span');
      lbl.className = 'hab-cd-lbl';
      lbl.textContent = u.label;
      seg.appendChild(num);
      seg.appendChild(lbl);
      grid.appendChild(seg);
      refs[u.key] = num;
    });
    host.textContent = '';
    host.appendChild(grid);
    return refs;
  }

  function expire(host, text){
    host.textContent = '';
    var p = document.createElement('p');
    p.className = 'hab-cd-expired';
    p.textContent = text || DEFAULT_EXPIRED;
    host.appendChild(p);
    host.setAttribute('data-expired', 'true');
  }

  function init(host){
    if(host.getAttribute('data-cd-ready') === '1') return;
    var tz = host.getAttribute('data-tz') || 'Europe/London';
    var target = isoToEpoch(host.getAttribute('data-end-iso'), tz);
    var expiredText = host.getAttribute('data-expired-text') || DEFAULT_EXPIRED;

    if(isNaN(target)){ expire(host, expiredText); return; } // no/invalid date → treat as ended
    host.setAttribute('data-cd-ready', '1');

    if(target - Date.now() <= 0){ expire(host, expiredText); return; }

    var refs = build(host);
    var lastSec = -1;

    function tick(){
      var diff = target - Date.now();
      if(diff <= 0){
        clearInterval(timer);
        expire(host, expiredText);
        return;
      }
      var totalSec = Math.floor(diff / 1000);
      var days = Math.floor(totalSec / 86400);
      var hours = Math.floor((totalSec % 86400) / 3600);
      var minutes = Math.floor((totalSec % 3600) / 60);
      var seconds = totalSec % 60;
      refs.days.textContent = pad(days);
      refs.hours.textContent = pad(hours);
      refs.minutes.textContent = pad(minutes);
      refs.seconds.textContent = pad(seconds);
      if(!reduce && seconds !== lastSec){
        refs.seconds.classList.remove('tick');
        void refs.seconds.offsetWidth; // reflow → restart the pulse
        refs.seconds.classList.add('tick');
      }
      lastSec = seconds;
    }

    tick();
    var timer = setInterval(tick, 1000);
  }

  function boot(){
    [].slice.call(document.querySelectorAll('[data-hab-countdown]')).forEach(init);
  }

  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
